import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { captureException, captureMessage } from '@sentry/react';
import { differenceInDays } from 'date-fns';
import { jwtDecode } from 'jwt-decode';
import { usePostHog } from 'posthog-js/react';
import {
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { coerce, satisfies } from 'semver';

import {
  notificationTokenClient,
  type RegisterNotificationTokenModel,
} from '@portal-clients/notification-tokens';
import type { PushNotificationModel } from '@portal-clients/push-notification';

import { getAuthToken } from '@frontend/auth';
import { useLicenses } from '@frontend/licenses';
import { usePortalPublicConfiguration } from '@frontend/portal-public-configuration';
import {
  MetadataContextState,
  routes,
  useMetadata,
  usePortalConfiguration,
  useUser,
} from '@frontend/utils';

import {
  consumePendingLaunchNotification,
  removeEarlyPushNotificationListener,
} from './pending-launch-notification';
import { PushNotificationTokenStorage } from './push-notification-token-storage';
import {
  PushNotificationsContext,
  type PushNotificationsContextValue,
} from './push-notifications-context';

const getDeliveredNotifications = async () => {
  const notificationList = await PushNotifications.getDeliveredNotifications();
  console.log('delivered notifications', notificationList);
};

const checkPushNotificationSupportedByBundle = (
  metadata: MetadataContextState,
): boolean => {
  if (typeof APP_CONFIG.pushNotificationsSemverFilter === 'boolean') {
    return APP_CONFIG.pushNotificationsSemverFilter;
  } else if (typeof APP_CONFIG.pushNotificationsSemverFilter === 'string') {
    // if the allowed is a string, we assume it's a semver range
    // and we need to check if the current version satisfies it

    if (
      metadata.appVersion === 'local' ||
      metadata.appVersion.includes('dev') ||
      metadata.appVersion.includes('latest')
    ) {
      return true;
    }

    const coercedVersion = coerce(metadata.appVersion);
    return satisfies(coercedVersion!, APP_CONFIG.pushNotificationsSemverFilter);
  }

  return false;
};

interface PortalSessionJwtPayload {
  refreshToken?: string;
}

const hashPushTokenSnapshot = async (
  pushToken: string,
  sessionRefreshToken: string,
): Promise<string> => {
  const payload = `${pushToken}-${sessionRefreshToken}`;

  const hashedTokenBuffer = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(payload),
  );

  return btoa(String.fromCharCode(...new Uint8Array(hashedTokenBuffer)));
};

export const PushNotificationsProvider = (props: PropsWithChildren) => {
  const hasTriggeredInitialization = useRef(false);
  const handledNotificationIds = useRef(new Set<string>());
  const metadata = useMetadata();
  const publicConfig = usePortalPublicConfiguration();
  const config = usePortalConfiguration();
  const {
    appPushNotifications: appPushNotificationsEnabled,
    news: newsEnabled,
    vouchers: vouchersLicense,
    stampCards: stampCardsLicense,
  } = useLicenses();
  const supportedByBundle = useMemo(() => {
    return checkPushNotificationSupportedByBundle(metadata);
  }, [metadata.appVersion]);
  const isSupported =
    appPushNotificationsEnabled === true &&
    publicConfig.pushNotificationsEnabled === true &&
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable('PushNotifications') &&
    supportedByBundle;

  const { registerLogoutCallback } = useUser();

  const [isTurningOnOrOff, setIsTurningOnOrOff] = useState<boolean>(false);
  const [listenersRegistered, setListenersRegistered] =
    useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [canRequestPermission, setCanRequestPermission] =
    useState<boolean>(false);
  const [registrationToken, setRegistrationToken] = useState<
    string | undefined
  >();

  const posthog = usePostHog();
  const navigate = useNavigate();

  const navigateToNotificationTarget = useCallback(
    (pushNotification: PushNotificationModel) => {
      const { notificationId, accountId, transactionId } =
        pushNotification.data;

      // notificationId is required to dedupe a tap — without it the dedupe
      // set can't be keyed, so we bail rather than risk double-navigation.
      // (see PushNotificationModel.data.notificationId for what it actually
      // identifies, which differs by notification type). transactionId is
      // unrelated to deduping; it's only used below to deep-link to a
      // specific wallet transaction, and isn't set for every notification
      // type (e.g. vouchers).
      if (!notificationId) {
        return;
      }

      if (handledNotificationIds.current.has(notificationId)) {
        return;
      }

      handledNotificationIds.current.add(notificationId);

      console.log(
        `Push notification action performed, notificationId: ${notificationId}, targetPage: ${pushNotification.data.targetPage ?? 'N/A'}`,
      );

      posthog?.capture('push_notification_interaction', {
        notification_id: notificationId,
      });

      if (!('targetPage' in pushNotification.data)) {
        navigate(routes.wallet.list());
        return;
      }

      if (
        pushNotification.data.targetPage === 'NEWS' &&
        publicConfig.features?.news?.enabled &&
        newsEnabled
      ) {
        navigate(routes.news());
        return;
      } else if (pushNotification.data.targetPage === 'SITE_UNITS') {
        navigate(routes.siteUnits.sites);
        return;
      } else if (
        pushNotification.data.targetPage === 'BENEFITS' &&
        ((vouchersLicense && config.config?.features?.voucher?.enabled) ||
          (stampCardsLicense && config.config?.features?.stampCard?.enabled))
      ) {
        navigate(routes.benefits);
        return;
      } else if (
        pushNotification.data.targetPage === 'WALLET' &&
        accountId &&
        transactionId
      ) {
        navigate(routes.wallet.transaction(accountId, transactionId));
        return;
      } else if (
        pushNotification.data.targetPage === 'WALLET' &&
        (accountId || transactionId)
      ) {
        // Only one of accountId/transactionId was provided, which shouldn't
        // happen. Capture so this is observable in production instead of
        // silently falling back to the wallet list below.
        captureMessage(
          'WALLET notification missing accountId or transactionId; falling back to wallet list',
          'warning',
        );
        navigate(routes.wallet.list());
        return;
      } else {
        navigate(routes.wallet.list());
      }
    },
    [
      posthog,
      navigate,
      publicConfig,
      newsEnabled,
      vouchersLicense,
      stampCardsLicense,
      config,
    ],
  );

  const navigateToNotificationTargetRef = useRef(navigateToNotificationTarget);
  useEffect(() => {
    navigateToNotificationTargetRef.current = navigateToNotificationTarget;
  }, [navigateToNotificationTarget]);

  const refreshStoredToken = useCallback(
    async (token: string, hashedToken: string) => {
      notificationTokenClient
        .register({
          token,
          platform:
            Capacitor.getPlatform() as RegisterNotificationTokenModel['platform'],
        })
        .then(async () => {
          // We make sure to update the local snapshot last in case the client request
          // fails. Otherwise we would have an inconsistent state where the frontend
          // believes the token is updated, but the backend still has the old one.
          await PushNotificationTokenStorage.set({
            hashedToken,
            createdAt: new Date(),
          });
        })
        .catch((error) => {
          captureException(error, {
            extra: { message: 'Error updating push notification token' },
          });
        });
    },
    [],
  );

  useEffect(() => {
    if (!isSupported) {
      removeEarlyPushNotificationListener().catch(console.error);
      return;
    }

    if (!config.config) {
      return;
    }

    const registrationListener = PushNotifications.addListener(
      'registration',
      async (token) => {
        console.log('Push notifications registered');
        setRegistrationToken(token.value);

        const authToken = await getAuthToken();

        if (!authToken) {
          return;
        }

        let decodedToken: PortalSessionJwtPayload;

        try {
          decodedToken = jwtDecode<PortalSessionJwtPayload>(authToken);
        } catch (error) {
          captureException(error, {
            extra: {
              message:
                'Failed to decode auth token for push notification registration',
            },
          });
          return;
        }

        const refreshToken =
          typeof decodedToken.refreshToken === 'string'
            ? decodedToken.refreshToken
            : undefined;

        if (!refreshToken) {
          return;
        }

        const hashedToken = await hashPushTokenSnapshot(
          token.value,
          refreshToken,
        );

        const storedValue = await PushNotificationTokenStorage.get();

        if (storedValue) {
          const { createdAt, hashedToken: existingHashToken } = storedValue;

          if (
            existingHashToken !== hashedToken ||
            differenceInDays(new Date(), createdAt) >= 7
          ) {
            console.info(
              'Push notification token has changed or is outdated, updating stored value',
            );

            return refreshStoredToken(token.value, hashedToken);
          }
        } else {
          return refreshStoredToken(token.value, hashedToken);
        }
      },
    );

    const registrationErrorListener = PushNotifications.addListener(
      'registrationError',
      (err) => {
        captureException(err, {
          level: 'error',
          extra: {
            message: 'Push notification registration error encountered',
          },
        });
      },
    );

    const receiveListener = PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        console.log('Push notification received: ', notification);
      },
    );

    const actionListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification) => {
        const pushNotification =
          notification.notification as PushNotificationModel;

        navigateToNotificationTargetRef.current(pushNotification);
      },
    );

    Promise.all([
      registrationListener,
      registrationErrorListener,
      receiveListener,
      actionListener,
    ])
      .then(() => {
        setListenersRegistered(true);

        const pending = consumePendingLaunchNotification();
        removeEarlyPushNotificationListener().catch(console.error);

        if (pending) {
          navigateToNotificationTargetRef.current(pending);
        }
      })
      .catch(console.error);

    return () => {
      registrationListener
        .then((listener) => listener.remove())
        .catch(console.error);
      registrationErrorListener
        .then((listener) => listener.remove())
        .catch(console.error);
      receiveListener
        .then((listener) => listener.remove())
        .catch(console.error);
      actionListener.then((listener) => listener.remove()).catch(console.error);
    };
  }, [isSupported, config.config]);

  const requestPermission: PushNotificationsContextValue['requestPermission'] =
    useCallback(async () => {
      let permStatus = await PushNotifications.checkPermissions();

      if (
        permStatus.receive === 'prompt' ||
        permStatus.receive === 'prompt-with-rationale'
      ) {
        permStatus = await PushNotifications.requestPermissions();
      }

      if (permStatus.receive !== 'granted') {
        return false;
      }

      return true;
    }, []);

  const turnOn: PushNotificationsContextValue['turnOn'] =
    useCallback(async () => {
      console.log('Push notifications turning on');
      setIsTurningOnOrOff(true);
      const permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive !== 'granted') {
        setInitialized(false);
        throw new Error(
          `Unable to turn on push notifications, permission not granted`,
        );
      }

      if (!listenersRegistered) {
        return;
      }

      await PushNotifications.register();
      setInitialized(true);
      setIsTurningOnOrOff(false);
    }, [listenersRegistered]);

  const turnOff: PushNotificationsContextValue['turnOff'] =
    useCallback(async () => {
      setIsTurningOnOrOff(true);

      console.log('Push notifications turning off');

      console.info('Unregistering push notifications');
      await PushNotifications.unregister().catch((error) => {
        captureException(error, {
          extra: {
            message: 'Plugin failed to unregister push notifications',
          },
        });
      });

      console.info('Removing push notification token from storage');
      await PushNotificationTokenStorage.remove();

      if (registrationToken) {
        console.info('Deleting push notification token from backend');
        await notificationTokenClient
          .unregister({ token: registrationToken })
          .catch((error) => {
            captureException(error, {
              extra: { message: 'Error unregistering push notification token' },
            });
          });

        setRegistrationToken(undefined);
      } else {
        console.info(
          'No notification token in provider state, skipping backend deletion',
        );
      }

      setInitialized(false);
      setIsTurningOnOrOff(false);
    }, [registrationToken]);

  useEffect(() => {
    if (!listenersRegistered) {
      return;
    }

    if (!isSupported) {
      setInitialized(false);
      return;
    }

    // React StrictMode causes a lot of issues due to extra evaluations being
    // triggered. Make sure to only initialize once. This is very much a hack
    // but I haven't found any good way to handle this with StrictMode in mind
    // since we are actually setting up native APIs that don't like being
    // initialized multiple times.
    if (hasTriggeredInitialization.current) {
      return;
    }

    hasTriggeredInitialization.current = true;
    setIsTurningOnOrOff(true);

    PushNotifications.checkPermissions()
      .then(async (permStatus) => {
        setCanRequestPermission(permStatus.receive !== 'denied');

        const storedToken = await PushNotificationTokenStorage.get();

        if (permStatus.receive === 'granted' && storedToken !== undefined) {
          return turnOn()
            .then(() => {
              setInitialized(true);
              getDeliveredNotifications().catch((error) => {
                console.error(error);
                captureException(error);
              });
            })
            .catch((error: unknown) => {
              setInitialized(false);
              const msg =
                error instanceof Error ? error.message : String(error);

              if (msg.includes('capacitorDidRegisterForRemoteNotifications')) {
                // Expected: OS did not complete APNS registration (e.g.
                // notifications blocked, network unavailable). Not a bug.
                captureMessage(msg, 'info');
                return;
              }

              captureException(error, {
                extra: { message: 'Error registering push notifications' },
              });
            });
        }
      })
      .catch((error) => {
        captureException(error, {
          extra: { message: 'Error checking push notifications permission' },
        });
      })
      .finally(() => setIsTurningOnOrOff(false));
  }, [listenersRegistered]);

  useEffect(() => {
    if (!isSupported || !initialized || !registrationToken) {
      return;
    }

    const unregister = registerLogoutCallback(async () => {
      await turnOff();
    });

    return unregister;
  }, [initialized, isSupported, registrationToken]);

  const contextValue = useMemo(
    (): PushNotificationsContextValue => ({
      isSupported,
      canRequestPermission,
      isOn: initialized,
      isTurningOnOrOff: isTurningOnOrOff && listenersRegistered,
      turnOn,
      turnOff,
      requestPermission,
    }),
    [
      isSupported,
      canRequestPermission,
      initialized,
      isTurningOnOrOff,
      listenersRegistered,
      turnOn,
      turnOff,
      requestPermission,
    ],
  );

  return (
    <PushNotificationsContext.Provider value={contextValue}>
      {props.children}
    </PushNotificationsContext.Provider>
  );
};
